import styled from 'styled-components';

import { SPACING } from '~/common/constants/spacing';

export const PromptItemRoot = styled.li<{ $visible?: boolean }>`
  position: relative;
  width: 100%;
  height: 320px;
  border-radius: 10px !important;
  overflow: visible;
  flex: 0 0 calc((100% - 60px) / 4); /* 4 colunas, 3 gaps de 20px = 60px */
  max-width: 300px;
  min-width: 264px;
  justify-self: center;
  -webkit-tap-highlight-color: transparent;
  -webkit-tap-highlight-color: transparent;
  border: 1px solid transparent;
  box-shadow: rgba(9, 30, 66, 0.25) 0px 4px 8px -2px,
    rgba(9, 30, 66, 0.08) 0px 0px 0px 1px;

  user-select: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '24px')});
  transition: opacity 500ms ease-out, transform 500ms ease-out;

  @media (max-width: 1260px) {
    flex: 0 0 calc((100% - 40px) / 3); /* 3 colunas, 2 gaps */
    max-width: none;
  }

  @media (max-width: 1030px) {
    flex: 0 0 calc((100% - 20px) / 2); /* 2 colunas, 1 gap */
  }

  @media (max-width: 580px) {
    flex: 0 0 calc((100% - 12px) / 2); /* 2 colunas no mobile */
    min-width: 0;
    max-width: none;
    height: 260px;
  }

  /* Reflexo especular (diagonal) */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 3;
    border-radius: inherit;
    overflow: hidden;
    background: linear-gradient(
      120deg,
      transparent 30%,
      rgba(255, 255, 255, 0.07) 45%,
      rgba(255, 255, 255, 0.07) 55%,
      transparent 70%
    );
    background-size: 250% 100%;
    background-position: 120% 0;
    opacity: 0;
    transition: opacity 220ms ease, background-position 500ms ease;

    pointer-events: none;
  }

  &:hover::after {
    opacity: 1;
    background-position: -20% 0;
  }

  > a {
    display: block;
    height: 100%;
    overflow: hidden;
    border-radius: 10px;
    > div {
      border-radius: 10px;
      > div {
        border-radius: 10px;
      }
    }
  }
`;

export const AdminFloatingMenuWrapper = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

export const MediaWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
`;

export const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 300ms ease;
`;

export const MediaVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* Gradiente pra facilitar leitura dos textos em cima */
export const MediaOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: transparent;
`;

/* Conteúdo em cima do background */
export const Content = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  padding: ${SPACING._12};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: #ffffff;
`;

/* Hover: só a imagem cresce, não o bloco */
export const Hoverable = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  cursor: pointer;

  &:hover ${MediaImage} {
    transform: scale(1.08);
  }
`;

export const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

export const GlassChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  font-size: 12px;
`;

export const AdminMenuTrigger = styled(GlassChip)<{ $open: boolean }>`
  border: none;
  outline: none;
  cursor: pointer;
  color: #ffffff;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 200ms ease, background 200ms ease;
  margin-top: -10px;
  margin-right: -10px;

  ${PromptItemRoot}:hover & {
    opacity: 1;
    pointer-events: auto;
  }

  @media (max-width: 768px) {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`;

export const AdminMenuDropdown = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 24px;
  right: 0;
  min-width: 132px;
  border-radius: 12px;
  padding: 6px;
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  flex-direction: column;
  gap: 4px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

export const AdminMenuItem = styled.button`
  width: 100%;
  border: none;
  outline: none;
  border-radius: 9999px;
  padding: 6px 10px;
  background: transparent;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: background 200ms ease;

  &:hover {
    background: rgba(0, 0, 0, 0.5);
  }
`;

export const TitleChip = styled(GlassChip)`
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;

  @media (max-width: 580px) {
    max-width: calc(100% - 50px);

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    }
  }
`;

export const TitleText = styled.span`
  font-weight: 600;
  font-size: 14px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1; /* 1 linha pra não quebrar o layout */
  -webkit-box-orient: vertical;
`;

export const ActionButtonsColumn = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 6px;
  flex-shrink: 0;
`;

export const LikesButton = styled(GlassChip)`
  border: none;
  outline: none;
  cursor: pointer;
  padding: 6px 10px;
  min-height: 28px;
  border-radius: 9999px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #ffffff;
  transition: 200ms;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`;

export const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const Description = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3; /* até 3 linhas */
  -webkit-box-orient: vertical;
`;

export const BottomRow = styled.div`
  display: flex;
  justify-content: start;
  gap: 6px;
`;

export const PlanTierBadge = styled(GlassChip)`
  border: none;
  outline: none;
  padding: 6px 10px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #ffffff;
  margin-left: auto;
`;

export const CopyButton = styled(GlassChip)`
  border: none;
  outline: none;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #ffffff;
  transition: 200ms;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`;
